import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { CreditCard, Loader2, ShieldCheck, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BRAND_LABEL = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express",
  discover: "Discover", jcb: "JCB", diners: "Diners", unionpay: "UnionPay",
};

function fmtBrand(b) {
  if (!b) return "Card";
  return BRAND_LABEL[String(b).toLowerCase()] || String(b).charAt(0).toUpperCase() + String(b).slice(1);
}

export default function SavedCardPanel() {
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/saved-card");
      setCard(data);
    } catch {
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // On mount, if URL has ?card_saved=1&session_id=..., call setup-status to persist
  useEffect(() => {
    const url = new URL(window.location.href);
    const cs = url.searchParams.get("card_saved");
    const sid = url.searchParams.get("session_id");
    if (cs === "1" && sid) {
      (async () => {
        try {
          const { data } = await api.get(`/me/payment-method/setup-status/${sid}`);
          if (data.saved) {
            toast.success(`Card saved — ${fmtBrand(data.brand)} ending ${data.last4}`);
            refresh();
          }
        } catch (e) {
          toast.error("Could not confirm saved card — please retry");
        } finally {
          url.searchParams.delete("card_saved");
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
        }
      })();
    }
  }, [refresh]);

  const addCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/payment-method/setup-session", {});
      if (data.setup_url) {
        window.location.href = data.setup_url;
      } else {
        toast.error("Could not start card setup");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Card setup failed");
      setBusy(false);
    }
  };

  const removeCard = async () => {
    if (!window.confirm("Remove your saved payment method?\n\nAutoPay will be disabled. You'll need to add a new card before your next scheduled visit.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/me/saved-card");
      if (data.removed) {
        toast.success("Payment method removed");
        refresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    } finally {
      setBusy(false);
    }
  };

  const toggleAutopay = async (nextEnabled) => {
    setBusy(true);
    try {
      await api.post("/me/autopay", { enabled: nextEnabled });
      toast.success(nextEnabled ? "AutoPay enabled" : "AutoPay disabled");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update AutoPay");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-2" data-testid="saved-card-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold flex items-center gap-2">
            <CreditCard className="w-3.5 h-3.5" /> Saved payment method
          </div>
          <p className="mt-1 text-xs text-gray-600">Manage your card on file for AutoPay on recurring visits.</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Powered by Stripe
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500" data-testid="saved-card-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && card?.payment_method_required && (
        <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="payment-method-required-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-bold">Payment Method Required</div>
            <div className="mt-0.5">Please add a new payment method before your next scheduled visit so we can process payment automatically.</div>
          </div>
        </div>
      )}

      {!loading && card?.has_card && (
        <div className="mt-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-4 flex items-center justify-between flex-wrap gap-3" data-testid="saved-card-row">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#062818] text-white flex items-center justify-center">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-extrabold text-gray-900" data-testid="saved-card-brand-last4">
                {fmtBrand(card.brand)} ending {card.last4 || "••••"}
              </div>
              <div className="text-xs text-gray-500">
                {card.exp_month && card.exp_year ? `Exp ${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}` : "Saved securely"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer" data-testid="saved-card-autopay-toggle-label">
              <input
                type="checkbox"
                checked={!!card.autopay_enabled}
                onChange={(e) => toggleAutopay(e.target.checked)}
                disabled={busy}
                className="w-4 h-4 accent-emerald-600"
                data-testid="saved-card-autopay-toggle"
              />
              AutoPay {card.autopay_enabled ? "enabled" : "disabled"}
            </label>
            <Button
              variant="outline"
              onClick={addCard}
              disabled={busy}
              className="rounded-full text-xs"
              data-testid="saved-card-replace-btn"
            >
              Replace card
            </Button>
            <Button
              variant="outline"
              onClick={removeCard}
              disabled={busy}
              className="rounded-full text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="saved-card-remove-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          </div>
        </div>
      )}

      {!loading && !card?.has_card && (
        <div className="mt-4 rounded-2xl border border-dashed border-gray-300 p-6 text-center" data-testid="saved-card-empty">
          <CreditCard className="w-8 h-8 mx-auto text-gray-400" />
          <div className="mt-2 text-sm font-bold text-gray-800">No card on file</div>
          <div className="mt-1 text-xs text-gray-500 max-w-md mx-auto">
            Add a payment method to enable AutoPay. Your card will be charged only after each visit is completed.
          </div>
          <Button
            onClick={addCard}
            disabled={busy}
            className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white"
            data-testid="saved-card-add-btn"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Add payment method
          </Button>
        </div>
      )}
    </div>
  );
}
